import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useFocusTrap } from "@client/hooks/useFocusTrap";

export type ModalSize = "sm" | "md" | "lg" | "xl";

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
  // The editor's input panels — grids of year rows and line-item cards that
  // were a 460px column until this redesign put them behind the icon rail.
  xl: "max-w-5xl",
};

interface ModalProps {
  open: boolean;
  title: string;
  /** A sentence under the title, announced with it as the dialog's description. */
  description?: ReactNode;
  size?: ModalSize;
  onClose: () => void;
  children: ReactNode;
  /** Right-aligned actions; the caller owns the buttons and their order. */
  footer?: ReactNode;
}

/**
 * Ported from the ZNMS admin's `Modal`, with the focus handling it never had.
 *
 * Still portalled to `<body>`: rendered in place, any ancestor with a CSS
 * transform — `.feature-card`'s hover lift, for one — becomes the containing
 * block for `position: fixed` and traps the overlay inside a card.
 */
export function Modal({
  open,
  title,
  description,
  size = "md",
  onClose,
  children,
  footer,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useFocusTrap(dialogRef, open);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    }
    window.addEventListener("keydown", onKeyDown);

    // The page behind must not scroll under the overlay: on a phone, scrolling
    // the backdrop moves the page and leaves the dialog sitting over nothing.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-[2px]"
      // A click that starts inside the dialog and ends on the backdrop (a drag
      // across a text selection) must not close it, so this listens for the
      // click rather than the mousedown and checks the target is the backdrop.
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={`island-shell flex max-h-[90vh] w-full flex-col gap-4 overflow-y-auto ${SIZE_CLASSES[size]}`}
      >
        <div className="flex flex-col gap-1">
          <h2 id={titleId} className="text-base font-bold text-ink">
            {title}
          </h2>
          {description && (
            <p id={descriptionId} className="text-sm text-ink-soft">
              {description}
            </p>
          )}
        </div>

        {children}

        {footer && <div className="flex flex-wrap justify-end gap-2">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
